import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useMemo, useState } from "react";
import ApiError from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/ApiError";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import Badge from "../models/Badge";
import BadgeService from "../services/BadgeService";
import { QueryKeyFactory } from "../services/queryKeys";
import { randomId } from "../utils/FileUtils";
import { unwrap } from "../utils/queryFn";
import { createTableQuery, refreshTableQuery } from "../utils/tableQuery";
import OpsImageService from "../services/OpsImageService";

export const badgeKeys = new QueryKeyFactory(BadgeService.path);

export const BADGE_DELETE_BLOCKED_MESSAGE = "You cannot delete a badge with existing certifications. Disable it instead.";

function isSqlForeignKeyMessage(message: string): boolean {
  const lower = message.toLowerCase();
  return lower.includes("foreign key constraint") || lower.includes("cannot delete or update a parent row");
}

function toBadgeDeleteError(error: unknown): never {
  if (error instanceof ApiError && isSqlForeignKeyMessage(error.message)) {
    throw new ApiError(error.errorCode, BADGE_DELETE_BLOCKED_MESSAGE, error.apiResponse);
  }
  throw error;
}

export const useBadgeList = (name: string) => {
  const queryClient = useQueryClient();
  const [forceUpdateOn, setForceUpdateOn] = useState<number>();
  const [isUploading, setIsUploading] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState<Badge | undefined>(undefined);
  const [badgeToDelete, setBadgeToDelete] = useState<Badge | undefined>(undefined);
  const [viewBadge, setViewBadge] = useState<Badge | undefined>();
  const [editBadge, setEditBadge] = useState<Badge | undefined>();
  const [editMode, setEditMode] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | undefined>(undefined);

  const onDataFetch = useMemo(
    () =>
      createTableQuery<Badge>({
        queryKey: (page, pageSize, sortBy, sortDir) => badgeKeys.list({ page, pageSize, sortBy, sortDir, name }),
        queryFn: (page, pageSize) => unwrap(BadgeService.getBadgeFilter(page, pageSize, name)),
        client: queryClient,
      }),
    [queryClient, name],
  );

  const refreshTable = () => refreshTableQuery(badgeKeys.lists(), () => setForceUpdateOn(Date.now()), queryClient);

  const createMutation = useMutation({
    mutationFn: (badge: Badge) => unwrap(BadgeService.create(badge)),
    onSuccess: async (badge) => {
      setSelectedFile("");
      await refreshTable();
      NotificationService.info(`Badge ${badge.name} created successfully`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (badge: Badge) => unwrap(BadgeService.update(badge)),
    onSuccess: async (badge) => {
      await refreshTable();
      NotificationService.info(`Badge ${badge.name} updated successfully`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (badge: Badge) => {
      try {
        return await unwrap(BadgeService.delete(badge));
      } catch (error) {
        toBadgeDeleteError(error);
      }
    },
    onSuccess: async () => {
      setBadgeToDelete(undefined);
      await refreshTable();
      NotificationService.info("Badge deleted successfully");
    },
    onError: () => {
      setBadgeToDelete(undefined);
    },
  });

  const changeStatusMutation = useMutation({
    mutationFn: ({ externalId, enable }: { externalId: string; enable: boolean }) =>
      unwrap(BadgeService.changeBadgeStatus(externalId, enable)),
    onSuccess: async () => {
      setSelectedBadge(undefined);
      await refreshTable();
      NotificationService.info("Badge updated!");
    },
  });

  const handleOpenViewDialog = (instance: Badge) => {
    setViewBadge(instance);
  };

  const handleOpenEditDialog = (badge: Badge) => {
    setEditBadge(badge);
    setEditMode(true);
  };

  const handleOnDeleteBadge = (badge?: Badge) => {
    const target = badge ?? badgeToDelete;
    if (!target) return;
    deleteMutation.mutate(target);
  };

  const handleOnCreateBadge = (badge: Badge) => {
    createMutation.mutate(badge);
  };

  const handleOnUpdate = (badge: Badge) => {
    updateMutation.mutate(badge);
  };

  const handleBadgeStatusChange = () => {
    if (!selectedBadge?.externalId) return;
    changeStatusMutation.mutate({
      externalId: selectedBadge.externalId,
      enable: !selectedBadge.enabled,
    });
  };

  const closeCreateDialog = () => {
    setOpenCreateModal(false);
    setSelectedFile(undefined);
  };

  const closeEditDialog = () => {
    setEditBadge(undefined);
    setEditMode(false);
    setSelectedFile(undefined);
  };

  const handleImageDelete = () => {
    setSelectedFile("");
  };

  const handleUploadImage = async (e: ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList) return;

    setIsUploading(true);
    const fileToUpload = fileList[0];
    const filename = `ensolvers-ops-site-image_${randomId()}`;
    try {
      const image = await unwrap(OpsImageService.uploadImage(fileToUpload, filename));
      setSelectedFile(image.url);
    } catch {
      NotificationService.error("This file can not be uploaded. Please try with a different one");
    } finally {
      setIsUploading(false);
    }
  };

  const isLoading =
    isUploading || createMutation.isPending || updateMutation.isPending || deleteMutation.isPending || changeStatusMutation.isPending;

  return {
    onDataFetch,
    forceUpdateOn,
    isLoading,
    openCreateModal,
    selectedFile,
    badgeActions: {
      setOpenCreateModal,
      setEditMode,
      setSelectedBadge,
      setBadgeToDelete,
      handleOpenViewDialog,
      setViewBadge,
      handleOnDeleteBadge,
      handleOpenEditDialog,
      setEditBadge,
      handleOnCreateBadge,
      handleOnUpdate,
      handleBadgeStatusChange,
      handleUploadImage,
      handleImageDelete,
      closeCreateDialog,
      closeEditDialog,
    },
    badgeItems: {
      selectedBadge,
      editMode,
      viewBadge,
      editBadge,
      badgeToDelete,
    },
  };
};
